import { Button, IconButton, Switch, TimePicker, useSnackbar } from '@hue/design-system';
import { Plus, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  weekdays,
  openingHoursError,
  type OpeningHourDraft,
} from './business-unit-opening-hours-model.js';

const MAX_PER_DAY = 3;

/// O que muda entre o horário da unidade e o expediente do profissional ou da sala.
export interface WeeklyHoursCopy {
  legend: ReactNode;
  hint: string;
  start: string;
  end: string;
  closed: string;
  copy: string;
  /// Nome acessível do botão de copiar, com o dia de origem.
  copyFrom: (day: string) => string;
  copied: string;
}

/// Horário de funcionamento do estabelecimento (ADR 0074): um dia fechado não tem faixa.
const openingHoursCopy: WeeklyHoursCopy = {
  legend: (
    <>
      Horário de funcionamento{' '}
      <span className="font-normal text-content-muted">· aparece no portal do cliente</span>
    </>
  ),
  hint: 'Só informa o cliente. As vagas da agenda vêm do expediente de cada profissional. Copiar substitui as faixas dos outros seis dias, inclusive os fechados; você pode ajustá-los antes de salvar.',
  start: 'Abre',
  end: 'Fecha',
  closed: 'Fechado',
  copy: 'Copiar funcionamento para os demais dias',
  copyFrom: (day) => `Copiar funcionamento de ${day} para os demais dias`,
  copied: 'Funcionamento copiado para os demais dias. Salve a unidade para aplicar.',
};

export function BusinessUnitOpeningHours(props: {
  value: OpeningHourDraft[];
  onChange: (value: OpeningHourDraft[]) => void;
  error?: string | undefined;
}) {
  return <WeeklyHours {...props} text={openingHoursCopy} />;
}

/// Faixas semanais, até três por dia: horário da unidade e expediente de profissional e sala.
export function WeeklyHours({
  value,
  onChange,
  error,
  text,
  disabled = false,
}: {
  value: OpeningHourDraft[];
  onChange: (value: OpeningHourDraft[]) => void;
  error?: string | undefined;
  text: WeeklyHoursCopy;
  disabled?: boolean;
}) {
  const snackbar = useSnackbar();
  function copyDay(weekday: number) {
    const day = value.filter((draft) => draft.weekday === weekday);
    onChange(
      weekdays.flatMap(({ value: target }) => day.map((draft) => ({ ...draft, weekday: target }))),
    );
    snackbar.success(text.copied);
  }

  function replaceDay(weekday: number, day: Array<Omit<OpeningHourDraft, 'weekday'>>) {
    onChange([
      ...value.filter((draft) => draft.weekday !== weekday),
      ...day.map((draft) => ({ ...draft, weekday })),
    ]);
  }

  return (
    <fieldset className="grid gap-3" disabled={disabled}>
      <legend className="mb-1 text-sm font-semibold">{text.legend}</legend>
      <p className="text-xs leading-5 text-content-muted">{text.hint}</p>
      <ul className="grid gap-2">
        {weekdays.map(({ value: weekday, label }) => {
          const day = value.filter((draft) => draft.weekday === weekday);
          return (
            <li
              key={weekday}
              className="grid gap-2 rounded-xl border border-surface-border px-3 py-2 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start"
            >
              <Switch
                checked={day.length > 0}
                onChange={(open) =>
                  replaceDay(weekday, open ? [{ start: '09:00', end: '18:00' }] : [])
                }
              >
                {label}
              </Switch>
              {day.length === 0 ? (
                <p className="text-sm text-content-muted sm:pt-2">{text.closed}</p>
              ) : (
                <div className="grid gap-2">
                  {day.map((draft, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[minmax(0,1fr)_2.5rem] items-end gap-2"
                    >
                      <div className="grid min-w-0 gap-2 min-[360px]:grid-cols-2">
                        <TimePicker
                          label={`${text.start} (${label.toLowerCase()})`}
                          value={draft.start || null}
                          onValueChange={(start) =>
                            replaceDay(
                              weekday,
                              day.map((item, at) =>
                                at === index ? { ...item, start: start ?? '' } : item,
                              ),
                            )
                          }
                        />
                        <TimePicker
                          label={`${text.end} (${label.toLowerCase()})`}
                          value={draft.end || null}
                          onValueChange={(end) =>
                            replaceDay(
                              weekday,
                              day.map((item, at) =>
                                at === index ? { ...item, end: end ?? '' } : item,
                              ),
                            )
                          }
                        />
                      </div>
                      {index === 0 ? (
                        <IconButton
                          label={`Adicionar faixa (${label.toLowerCase()})`}
                          icon={<Plus />}
                          disabled={day.length >= MAX_PER_DAY}
                          onClick={() =>
                            replaceDay(weekday, [...day, { start: day.at(-1)!.end, end: '' }])
                          }
                        />
                      ) : (
                        <IconButton
                          label={`Remover faixa (${label.toLowerCase()})`}
                          icon={<Trash2 />}
                          tone="danger"
                          onClick={() =>
                            replaceDay(
                              weekday,
                              day.filter((_, at) => at !== index),
                            )
                          }
                        />
                      )}
                    </div>
                  ))}
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-auto min-h-11 max-w-full whitespace-normal py-2"
                      aria-label={text.copyFrom(label.toLowerCase())}
                      disabled={Boolean(openingHoursError(day))}
                      onClick={() => copyDay(weekday)}
                    >
                      {text.copy}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
